import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Avatar, IconButton, Collapse, Card, CardContent, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import TradeFormFields from '../trades/TradeFormFields';
import { calculateTradeMetrics } from '../../utils/tradeUtils';

const StrategyPerformance = ({ filteredTrades = [], onTradeUpdate }) => {
  const [trades, setTrades] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [strategyInsights, setStrategyInsights] = useState([]);
  const [expandedStrategy, setExpandedStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editTradeOpen, setEditTradeOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (filteredTrades.length > 0 && strategies.length > 0) {
      generateStrategyInsights(filteredTrades, strategies);
    }
  }, [filteredTrades, strategies]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const [tradesRes, strategiesRes] = await Promise.all([
        axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTrades(tradesRes.data);
      setStrategies(strategiesRes.data);
      const dataToUse = filteredTrades.length > 0 ? filteredTrades : tradesRes.data;
      generateStrategyInsights(dataToUse, strategiesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTrade = (trade) => {
    setEditingTrade({
      ...trade,
      category: trade.category || 'stock',
      leverage: Number(trade.leverage) || 1,
      positionSize: Number(trade.positionSize) || '',
      strategyIds: trade.strategyIds || [],
      tags: trade.tags || '',
      rating: trade.rating || 3,
      exchange: trade.exchange || ''
    });
    setEditTradeOpen(true);
  };

  const handleTradeChange = (field, value) => {
    setEditingTrade(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveTrade = async () => {
    try {
      const token = localStorage.getItem('idToken');
      
      // Calculate metrics using shared utility function
      const metrics = calculateTradeMetrics(
        editingTrade.entryPrice,
        editingTrade.stopLoss,
        editingTrade.exitPrice,
        editingTrade.positionSize,
        editingTrade.leverage
      );
      
      const payload = {
        ...editingTrade,
        ...metrics,
        rrSummary: metrics.riskRewardRatio ? `R:R = ${metrics.riskRewardRatioFormatted || '1:1'} | Risk: $${metrics.riskAmount} | Reward: $${metrics.rewardAmount} | Quantity: ${metrics.quantity}` : ''
      };
      
      console.log('Saving trade with payload:', payload);
      
      const response = await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Trade save response:', response.data);
      
      setEditTradeOpen(false);
      setEditingTrade(null);
      await fetchData();
      // Trigger parent component refresh
      if (onTradeUpdate) {
        await onTradeUpdate();
      }
    } catch (err) {
      console.error('Error updating trade:', err);
      console.error('Error details:', err.response?.data || err.message);
    }
  };

  const handleDeleteTrade = async () => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/deleteTrade', {
        tradeId: editingTrade.tradeId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditTradeOpen(false);
      setEditingTrade(null);
      await fetchData();
      if (onTradeUpdate) {
        await onTradeUpdate();
      }
    } catch (err) {
      console.error('Error deleting trade:', err);
    }
  };

  // Format risk-reward ratio as 1:X
  const formatRiskRewardRatio = (ratio) => {
    if (!ratio || ratio <= 0) return '1:1';
    return `1:${ratio.toFixed(1)}`.replace('.0', '');
  };
  
  const generateStrategyInsights = (tradesData, strategiesData) => {
    const strategyMap = {};
    strategiesData.forEach(strategy => {
      strategyMap[strategy.strategyId] = strategy.name;
    });

    const strategyStats = {};
    
    tradesData.forEach(trade => {
      if (!trade.strategyIds || trade.strategyIds.length === 0) return;
      
      trade.strategyIds.forEach(strategyId => {
        if (!strategyStats[strategyId]) {
          strategyStats[strategyId] = {
            strategyId,
            name: strategyMap[strategyId] || 'Unknown Strategy',
            totalTrades: 0,
            wins: 0,
            losses: 0,
            totalPnL: 0,
            avgRating: 0,
            symbols: new Set(),
            categories: new Set(),
            lastTradeDate: null,
            avgRiskReward: 0,
            riskRewardSum: 0,
            ratingSum: 0
          };
        }
        
        const stats = strategyStats[strategyId];
        stats.totalTrades++;
        stats.symbols.add(trade.symbol);
        stats.categories.add(trade.category);
        
        if (trade.status === 'WIN') stats.wins++;
        if (trade.status === 'LOSS') stats.losses++;
        
        const pnl = parseFloat(trade.pnl || trade.profitLoss || 0);
        if (!isNaN(pnl)) stats.totalPnL += pnl;
        
        const rating = parseFloat(trade.rating || 0);
        if (rating > 0) stats.ratingSum += rating;
        
        const rr = parseFloat(trade.riskRewardRatio || 0);
        if (rr > 0) stats.riskRewardSum += rr;
        
        if (!stats.lastTradeDate || new Date(trade.tradeDate) > new Date(stats.lastTradeDate)) {
          stats.lastTradeDate = trade.tradeDate;
        }
      });
    });
    
    const insights = Object.values(strategyStats).map(stats => {
      const rawRiskReward = stats.totalTrades > 0 ? (stats.riskRewardSum / stats.totalTrades) : 0;
      return {
        ...stats,
        winRate: stats.totalTrades > 0 ? ((stats.wins / stats.totalTrades) * 100).toFixed(1) : 0,
        avgRating: stats.totalTrades > 0 ? (stats.ratingSum / stats.totalTrades).toFixed(1) : 0,
        avgRiskReward: stats.totalTrades > 0 ? (stats.riskRewardSum / stats.totalTrades).toFixed(2) : 0,
        avgRiskRewardFormatted: formatRiskRewardRatio(rawRiskReward),
        symbols: Array.from(stats.symbols),
        categories: Array.from(stats.categories)
      };
    }).sort((a, b) => b.totalTrades - a.totalTrades);
    
    setStrategyInsights(insights);
  };

  const getPerformanceColor = (pnl) => {
    if (pnl > 0) return '#4caf50';
    if (pnl < 0) return '#f44336';
    return '#ff9800';
  };

  const getWinRateColor = (winRate) => {
    if (winRate >= 70) return '#4caf50';
    if (winRate >= 50) return '#ff9800';
    return '#f44336';
  };

  if (loading) return <Typography>Loading strategy performance...</Typography>;

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Active Strategies</Typography>
              <Typography variant="h3" color="primary">{strategyInsights.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Most Used</Typography>
              <Typography variant="h5">{strategyInsights[0]?.name || 'N/A'}</Typography>
              <Typography variant="body2">{strategyInsights[0]?.totalTrades || 0} trades</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Best Performer</Typography>
              <Typography variant="h5">
                {strategyInsights.sort((a, b) => b.totalPnL - a.totalPnL)[0]?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4caf50' }}>
                ${strategyInsights.sort((a, b) => b.totalPnL - a.totalPnL)[0]?.totalPnL.toFixed(2) || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Strategy Performance</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Strategy</TableCell>
              <TableCell>Trades</TableCell>
              <TableCell>Win Rate</TableCell>
              <TableCell>P&L</TableCell>
              <TableCell>Avg R:R</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Symbols</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {strategyInsights.map((insight) => (
              <React.Fragment key={insight.strategyId}>
                <TableRow hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => setExpandedStrategy(expandedStrategy === insight.strategyId ? null : insight.strategyId)}
                      >
                        {expandedStrategy === insight.strategyId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                        {insight.name.charAt(0)}
                      </Avatar>
                      <Typography variant="subtitle2">
                        {insight.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={insight.totalTrades} 
                      size="small" 
                      icon={<ShowChartIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${insight.winRate}%`}
                      size="small"
                      sx={{ bgcolor: getWinRateColor(insight.winRate), color: 'white' }}
                      icon={insight.winRate >= 50 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ color: getPerformanceColor(insight.totalPnL), fontWeight: 'bold' }}
                    >
                      ${insight.totalPnL.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>{insight.avgRiskRewardFormatted || '1:1'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${insight.avgRating}/5`} 
                      size="small" 
                      color={insight.avgRating >= 4 ? 'success' : insight.avgRating >= 3 ? 'warning' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    {insight.symbols.slice(0, 3).map(symbol => (
                      <Chip key={symbol} label={symbol} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                    {insight.symbols.length > 3 && (
                      <Chip label={`+${insight.symbols.length - 3}`} size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label="View Details" 
                      size="small" 
                      color="primary" 
                      onClick={() => setExpandedStrategy(expandedStrategy === insight.strategyId ? null : insight.strategyId)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                </TableRow>
                
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={expandedStrategy === insight.strategyId} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Recent Trades for {insight.name}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Symbol</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Entry</TableCell>
                              <TableCell>Exit</TableCell>
                              <TableCell>P&L</TableCell>
                              <TableCell>Rating</TableCell>
                              <TableCell>Edit</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(filteredTrades.length > 0 ? filteredTrades : trades)
                              .filter(trade => trade.strategyIds?.includes(insight.strategyId))
                              .slice(0, 5)
                              .map((trade) => (
                              <TableRow key={trade.tradeId}>
                                <TableCell>{trade.tradeDate}</TableCell>
                                <TableCell>{trade.symbol}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={trade.status} 
                                    size="small" 
                                    color={trade.status === 'WIN' ? 'success' : trade.status === 'LOSS' ? 'error' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>${trade.entryPrice}</TableCell>
                                <TableCell>${trade.exitPrice || 'N/A'}</TableCell>
                                <TableCell sx={{ color: parseFloat(trade.profitLoss) >= 0 ? 'green' : 'red' }}>
                                  ${trade.profitLoss || '0'}
                                </TableCell>
                                <TableCell>{trade.rating || 'N/A'}/5</TableCell>
                                <TableCell>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleEditTrade(trade)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedStrategy?.name} - All Trades
          <IconButton onClick={() => setDetailsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Entry</TableCell>
                <TableCell>Exit</TableCell>
                <TableCell>P&L</TableCell>
                <TableCell>R:R</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(filteredTrades.length > 0 ? filteredTrades : trades)
                .filter(trade => trade.strategyIds?.includes(selectedStrategy?.strategyId))
                .map((trade) => (
                <TableRow key={trade.tradeId}>
                  <TableCell>{trade.tradeDate}</TableCell>
                  <TableCell>{trade.symbol}</TableCell>
                  <TableCell>
                    <Chip 
                      label={trade.status} 
                      size="small" 
                      color={trade.status === 'WIN' ? 'success' : trade.status === 'LOSS' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>${trade.entryPrice}</TableCell>
                  <TableCell>${trade.exitPrice || 'N/A'}</TableCell>
                  <TableCell sx={{ color: parseFloat(trade.profitLoss) >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                    ${trade.profitLoss || '0'}
                  </TableCell>
                  <TableCell>{trade.riskRewardRatioFormatted || formatRiskRewardRatio(trade.riskRewardRatio) || '1:1'}</TableCell>
                  <TableCell>{trade.rating || 'N/A'}/5</TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setDetailsOpen(false);
                        handleEditTrade(trade);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
      
      <Dialog open={editTradeOpen} onClose={() => setEditTradeOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Edit Trade - {editingTrade?.symbol}
        </DialogTitle>
        <DialogContent sx={{ pt: 6, pb: 3 }}>
          {editingTrade && (
            <Box sx={{ mt: 2 }}>
              <TradeFormFields
                values={editingTrade}
                onChange={handleTradeChange}
                strategies={strategies}
                trades={trades}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setEditTradeOpen(false)}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTrade}
            sx={{ minWidth: 100 }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteTrade}
            startIcon={<DeleteIcon />}
            sx={{ minWidth: 100 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StrategyPerformance;